import { Component, Element, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { INDICATOR_DIRECTIONS, IndicatorDirection } from '../../models/directions.types';
import { UUId } from '../../utils/utils';
import { TAB_TYPES, TabTypes } from './models/tabs.types';

@Component({
  tag: 'wink-tabs',
  styleUrl: 'styles/tabs.default.scss',
  shadow: true,
})
export class Tabs {
  @Element() host!: HTMLWinkTabsElement;

  @Prop() appearance: TabTypes = TAB_TYPES.container;
  @Prop() tabListAriaLabel = 'Tab navigation';
  @Prop() scrollLeftAriaLabel = 'Scroll tabs left';
  @Prop() scrollRightAriaLabel = 'Scroll tabs right';

  @State() activeTabIndex = 0;
  @State() isScrollable = false;
  @State() tabElements: HTMLElement[] = [];
  @State() uuidMap = new Map();
  @State() statusMessage = '';

  @Event() winkTabClicked!: EventEmitter<number>;
  @Event() winkTabIndicatorClicked!: EventEmitter<string[]>;

  private tabList!: HTMLElement;
  private tabItems!: NodeListOf<HTMLElement>;
  private mutationObserver: MutationObserver;

  private readonly handleClickOnButton = (index: number) => {
    const previousIndex = this.activeTabIndex;
    this.activeTabIndex = index;
    this.handleScroll();
    this.tabItems[this.activeTabIndex].focus();
    this.winkTabClicked.emit(this.activeTabIndex as number);

    if (previousIndex !== index) {
      const tabElement = this.tabElements[index];
      const tabTitle = tabElement?.dataset.title || `Tab ${index + 1}`;
      this.announceTabChange(tabTitle, index + 1, this.tabElements.length);
    }
  };

  handleKeyDown = (e: { key: string }) => {
    this.assignTheRightActiveTabIndex(e.key);
  };

  handleKeyDownIndicator = (e: { key: string }, direction: IndicatorDirection) => {
    if (e.key === 'Enter') {
      direction === INDICATOR_DIRECTIONS.left
        ? this.handleScroll(INDICATOR_DIRECTIONS.left)
        : this.handleScroll(INDICATOR_DIRECTIONS.right);
    }
  };

  announceTabChange(tabTitle: string, currentIndex: number, totalTabs: number): void {
    this.statusMessage = `${tabTitle}, ${currentIndex} of ${totalTabs} selected`;

    setTimeout(() => {
      this.statusMessage = '';
    }, 1000);
  }

  assignTheRightActiveTabIndex(key: string): void {
    const previousIndex = this.activeTabIndex;
    const supportedKeysMap = new Map<string, () => void>([
      [
        'ArrowRight',
        () => {
          this.handleScroll(INDICATOR_DIRECTIONS.right);

          if (previousIndex !== this.activeTabIndex) {
            const tabElement = this.tabElements[this.activeTabIndex];
            const tabTitle = tabElement?.dataset.title || `Tab ${this.activeTabIndex + 1}`;
            this.announceTabChange(tabTitle, this.activeTabIndex + 1, this.tabElements.length);
          }
        },
      ],
      [
        'ArrowLeft',
        () => {
          this.handleScroll(INDICATOR_DIRECTIONS.left);

          if (previousIndex !== this.activeTabIndex) {
            const tabElement = this.tabElements[this.activeTabIndex];
            const tabTitle = tabElement?.dataset.title || `Tab ${this.activeTabIndex + 1}`;
            this.announceTabChange(tabTitle, this.activeTabIndex + 1, this.tabElements.length);
          }
        },
      ],
      [
        'Home',
        () => {
          if (this.activeTabIndex !== 0) {
            this.activeTabIndex = 0;

            const tabElement = this.tabElements[0];
            const tabTitle = tabElement?.dataset.title || 'Tab 1';
            this.announceTabChange(tabTitle, 1, this.tabElements.length);
          }
        },
      ],
      [
        'End',
        () => {
          if (this.activeTabIndex !== this.tabElements.length - 1) {
            this.activeTabIndex = this.tabElements.length - 1;

            const tabElement = this.tabElements[this.activeTabIndex];
            const tabTitle = tabElement?.dataset.title || `Tab ${this.activeTabIndex + 1}`;
            this.announceTabChange(tabTitle, this.activeTabIndex + 1, this.tabElements.length);
          }
        },
      ],
    ]);

    const handler = supportedKeysMap.get(key);

    if (handler) {
      handler();
    }
  }

  private findNextTabItemId(direction: IndicatorDirection) {
    if (direction === INDICATOR_DIRECTIONS.left) {
      for (let i = this.activeTabIndex - 1; i >= 0; i--) {
        const nextItem = this.tabList.querySelector('#tab-' + i);
        if (nextItem && !nextItem.hasAttribute('disabled')) {
          return i;
        }
      }
    } else {
      for (let i = this.activeTabIndex + 1; i < this.tabElements.length; i++) {
        const nextItem = this.tabList.querySelector('#tab-' + i);
        if (nextItem && !nextItem.hasAttribute('disabled')) {
          return i;
        }
      }
    }
    return undefined;
  }

  handleScroll(direction?: IndicatorDirection) {
    if (direction) {
      const nextTabIndex = this.findNextTabItemId(direction);
      if (nextTabIndex !== undefined) {
        this.activeTabIndex = nextTabIndex;
      }
      this.winkTabIndicatorClicked.emit([direction, this.activeTabIndex.toString()]);
      requestAnimationFrame(() => {
        this.tabItems?.[this.activeTabIndex]?.focus();
      });
    }

    const containerRect = this.tabList.getBoundingClientRect();
    const containerCenter = containerRect.width / 2;
    const activeTab = this.tabList.querySelector('#tab-' + this.activeTabIndex);

    if (activeTab) {
      const activeRect = activeTab.getBoundingClientRect();
      const activeCenter = activeRect.left + activeRect.width / 2;
      const scrollOffset = activeCenter - containerCenter;

      let scrollLeft = this.tabList.scrollLeft + scrollOffset - 8;
      if (this.activeTabIndex === 0) {
        scrollLeft = 0;
      }

      if (this.activeTabIndex === this.tabElements.length - 1) {
        scrollLeft = this.tabList.scrollWidth;
      }

      this.tabList.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  }

  private checkIfScrollable() {
    this.tabList = this.host?.shadowRoot?.querySelector('.tab-list') as HTMLElement;
    this.tabItems = this.host?.shadowRoot?.querySelectorAll('.tab') as NodeListOf<HTMLElement>;

    let tabitemWidth = 0;
    this.tabItems.forEach((item) => {
      tabitemWidth = tabitemWidth + item.offsetWidth;
    });

    if (tabitemWidth >= this.tabList.offsetWidth + 4) {
      this.isScrollable = true;
    } else {
      this.isScrollable = false;
    }
  }

  private renderSlots(type: string) {
    return this.tabElements.map((item, index) => {
      const slotname = (item.slot && item.slot.replace('tab-', '')) || `${index + 1}`;
      const computedSlotName = type === 'tabpanel' ? `tabpanel-${slotname}` : `tab-${slotname}`;

      let uuid = this.uuidMap.get(computedSlotName);
      if (uuid === undefined) {
        uuid = UUId();
        this.uuidMap.set(computedSlotName, uuid);
      }

      return type === 'tabpanel' ? (
        <div
          key={uuid}
          id={`tabpanel-${index}`}
          role="tabpanel"
          aria-labelledby={`tab-${index}`}
          aria-hidden={this.activeTabIndex !== index}
          tabIndex={this.activeTabIndex === index ? 0 : -1}
          class={{ 'is-hidden': this.activeTabIndex !== index }}>
          <slot name={`tab-${slotname}`}></slot>
        </div>
      ) : (
        <button
          key={uuid}
          id={`tab-${index}`}
          type="button"
          role="tab"
          disabled={item.hasAttribute('disabled')}
          class={`tab ${this.activeTabIndex === index ? 'is-active-tab' : ''}`}
          aria-selected={`${this.activeTabIndex === index}`}
          aria-controls={`tabpanel-${index}`}
          tabIndex={this.activeTabIndex === index ? 0 : -1}
          onClick={() => this.handleClickOnButton(index)}
          onKeyDown={this.handleKeyDown}>
          <div class="tab-content">
            {item.dataset.icon && <wink-icon icon={item.dataset.icon}></wink-icon>}
            <div class="tab-titles">
              <p class="tab-title"> {item.dataset.title}</p>
              <p class="tab-subtitle">{item.dataset.subtitle}</p>
            </div>
          </div>
        </button>
      );
    });
  }

  async componentWillLoad() {
    const tabDivs = this.host?.querySelectorAll<HTMLElement>('div[slot*="tab-"]');
    this.tabElements = Array.from(tabDivs ?? []);
  }

  componentDidLoad() {
    this.checkIfScrollable();

    this.mutationObserver = new MutationObserver(() => {
      this.forceUpdate();
    });

    this.tabElements.forEach((el) => {
      this.mutationObserver.observe(el, { attributes: true, attributeFilter: ['data-title', 'data-subtitle'] });
    });
  }

  disconnectedCallback() {
    this.mutationObserver?.disconnect();
  }

  private forceUpdate() {
    const tabDivs = this.host?.querySelectorAll<HTMLElement>('div[slot*="tab-"]');
    this.tabElements = Array.from(tabDivs ?? []);
    this.checkIfScrollable();
  }

  render() {
    return (
      <Host>
        <div class={`tabs tabs-${this.appearance}`}>
          <div class={`tab-header ${this.isScrollable ? 'is-scrollable' : ''}`}>
            <div class="tab-list" role="tablist" aria-label={this.tabListAriaLabel} ref={(el) => (this.tabList = el as HTMLDivElement)}>
              {this.renderSlots('tab')}
            </div>

            {this.isScrollable && this.activeTabIndex > 0 && (
              <wink-indicator
                class="scroll-button scroll-button-left"
                direction="left"
                aria-label={this.scrollLeftAriaLabel}
                tabIndex={0}
                onKeyDown={(event) => this.handleKeyDownIndicator(event, INDICATOR_DIRECTIONS.left)}
                onClick={() => this.handleScroll(INDICATOR_DIRECTIONS.left)}></wink-indicator>
            )}
            {this.isScrollable && this.activeTabIndex !== this.tabElements.length - 1 && (
              <wink-indicator
                class="scroll-button scroll-button-right"
                direction="right"
                aria-label={this.scrollRightAriaLabel}
                tabIndex={0}
                onKeyDown={(event) => this.handleKeyDownIndicator(event, INDICATOR_DIRECTIONS.right)}
                onClick={() => this.handleScroll(INDICATOR_DIRECTIONS.right)}></wink-indicator>
            )}
          </div>

          <div class="sr-only" aria-live="polite" aria-atomic="true">
            {this.statusMessage}
          </div>

          <div aria-live="polite" aria-atomic="true">
            {this.renderSlots('tabpanel')}
          </div>
        </div>
      </Host>
    );
  }
}
